/*
  <promo-countdown data-ends="1788231599">
    Contador da promo "Leve 2". Fonte da data: o snippet promo-leve2.liquid,
    que passa o mesmo epoch usado no gate Liquid e no endsAt do BXGY.

  Contrato mantido da versão anterior:
    - elemento `<promo-countdown>` com `data-ends`
    - ao expirar, remove o `closest('[data-promo-expires]')`
    - continua correto quando a aba volta do segundo plano

  O que mudou (ênfase):
    - conta de segundo em segundo, com o dígito trocando animado
    - formato em blocos (dias / horas / min / seg) em vez de "Xd XXh XXm"
    - a cor esquenta conforme o prazo aperta: dourado > 24 h, âmbar <= 24 h,
      vermelho <= 6 h (aí o bloco também pulsa)

  Atributos opcionais:
    data-size="compact"    inline, para a tarja do PDP e a barra do topo
                           (padrão: "full", em blocos, para o banner da home)
    data-surface="light"   tons escurecidos para fundo claro (carrinho)
                           (padrão: fundo escuro, bordô)
    data-label="..."       texto curto antes do contador (ex.: "Termina em")

  Ajuste fino sem tocar neste arquivo: as cores saem das custom properties
  --pc-tone-calm / --pc-tone-warm / --pc-tone-hot, que o CSS do snippet
  pode sobrescrever.
*/

(() => {
  const SEGUNDO = 1000;
  const MINUTO = 60 * SEGUNDO;
  const HORA = 60 * MINUTO;
  const DIA = 24 * HORA;

  // Limiares de urgência. Acima de LIMITE_MORNO o contador é institucional
  // (dourado da marca); abaixo de LIMITE_QUENTE ele vira alarme.
  const LIMITE_MORNO = 24 * HORA;
  const LIMITE_QUENTE = 6 * HORA;

  const semMovimento = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  /* ---------------------------------------------------------------- CSS --

     Injetado uma única vez, por JS, porque este arquivo precisa funcionar
     sozinho — o snippet que renderiza o elemento pode não ter estilo pro
     contador. Namespace `pc__` pra não colidir com o CSS do snippet.       */

  const CSS = `
    promo-countdown {
      --pc-tone-calm: #d0ba8d;
      --pc-tone-warm: #f0a04b;
      --pc-tone-hot: #ff5b4a;
      --pc-tone: var(--pc-tone-calm);

      display: inline-flex;
      align-items: center;
      gap: 0.5em;
      color: var(--pc-tone);
      font-variant-numeric: tabular-nums;
      /* Trava a largura do dígito: sem isso a linha "anda" a cada segundo. */
      font-feature-settings: 'tnum' 1;
      line-height: 1;
    }

    /* Fundo claro (carrinho): os mesmos três estágios, escurecidos pra manter
       contraste >= 4.5:1 em texto pequeno sobre off-white. */
    promo-countdown[data-surface='light'] {
      --pc-tone-calm: #8a6d3b;
      --pc-tone-warm: #a8580f;
      --pc-tone-hot: #c62828;
    }

    promo-countdown[data-heat='warm'] { --pc-tone: var(--pc-tone-warm); }
    promo-countdown[data-heat='hot']  { --pc-tone: var(--pc-tone-hot); }

    promo-countdown:not([data-ready]) { visibility: hidden; }

    .pc__label {
      font-size: 0.82em;
      font-weight: 500;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      opacity: 0.85;
    }

    /* Bolinha que pisca — o sinal de "isto está correndo agora" que se lê
       antes mesmo de o número mudar. */
    .pc__dot {
      flex: none;
      width: 0.42em;
      height: 0.42em;
      border-radius: 50%;
      background: currentColor;
      animation: pc-dot 1s steps(1, end) infinite;
    }

    @keyframes pc-dot {
      0%, 55% { opacity: 1; }
      56%, 100% { opacity: 0.25; }
    }

    /* ---- formato em blocos (banner) ---- */

    .pc__blocks {
      display: inline-flex;
      align-items: flex-start;
      gap: 0.4em;
    }

    .pc__unit {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 0.35em;
      min-width: 2.4em;
      padding: 0.42em 0.3em 0.38em;
      border: 1px solid color-mix(in srgb, currentColor 45%, transparent);
      border-radius: 4px;
      background: color-mix(in srgb, currentColor 12%, transparent);
    }

    .pc__digits {
      display: block;
      font-size: 1.55em;
      font-weight: 800;
      letter-spacing: -0.02em;
      line-height: 0.9;
      /* Cada dígito é animado dentro deste box; overflow escondido faz a
         troca parecer um flip de placar em vez de um texto pulando. */
      overflow: hidden;
    }

    .pc__unit-name {
      display: block;
      font-size: 0.6em;
      font-weight: 500;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      opacity: 0.72;
    }

    /* ---- formato inline (tarja, barra do topo, carrinho) ---- */

    .pc__inline {
      display: inline-flex;
      align-items: baseline;
      gap: 0.3em;
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    /* Na tarja e na barra do topo o box de placar não cabe: 0,72 rem num
       header sticky vira duas linhas. Aqui a unidade volta a ser texto
       corrido — "02d 11h 59min 58s" — e o destaque fica só na cor. */
    .pc__inline .pc__unit {
      display: inline;
      min-width: 0;
      padding: 0;
      border: 0;
      border-radius: 0;
      background: none;
    }

    .pc__inline .pc__digits {
      display: inline-block;
      overflow: hidden;
      font-size: 1em;
      font-weight: 700;
      vertical-align: baseline;
    }

    .pc__inline .pc__unit-name {
      display: inline;
      font-size: 0.78em;
      font-weight: 500;
      letter-spacing: 0.04em;
      opacity: 0.75;
    }

    /* Nas últimas 6 h o bloco inteiro respira, não só a bolinha. */
    promo-countdown[data-heat='hot'] .pc__blocks,
    promo-countdown[data-heat='hot'] .pc__inline {
      animation: pc-pulse 1.6s ease-in-out infinite;
    }

    @keyframes pc-pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.035); }
    }

    .pc__sr {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip-path: inset(50%);
      overflow: hidden;
      white-space: nowrap;
    }

    @media (prefers-reduced-motion: reduce) {
      .pc__dot,
      promo-countdown[data-heat='hot'] .pc__blocks,
      promo-countdown[data-heat='hot'] .pc__inline {
        animation: none;
      }
    }

    /* Por último e com o elemento no seletor: precisa vencer as duas
       regras de display da unidade, que vêm antes com a mesma força. */
    promo-countdown .pc__unit[hidden] { display: none; }

    @media screen and (max-width: 749px) {
      .pc__unit {
        min-width: 2.15em;
        padding: 0.36em 0.24em 0.32em;
      }
    }
  `;

  let cssInjetado = false;
  function injetarCSS() {
    if (cssInjetado) return;
    cssInjetado = true;
    const style = document.createElement('style');
    style.setAttribute('data-promo-countdown', '');
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* --------------------------------------------------------------- data --

     `data-ends` aceita epoch em segundos (o que o Liquid entrega), epoch em
     milissegundos ou uma data ISO. Sem isso, um dia alguém passa ISO e o
     contador some sem erro no console.                                     */

  function resolverFim(bruto) {
    if (!bruto) return NaN;
    const texto = String(bruto).trim();
    if (/^\d+$/.test(texto)) {
      const n = Number(texto);
      return n > 1e12 ? n : n * 1000;
    }
    return Date.parse(texto);
  }

  const doisDigitos = (n) => String(n).padStart(2, '0');

  class PromoCountdown extends HTMLElement {
    connectedCallback() {
      if (this.montado) return;
      this.montado = true;

      this.fim = resolverFim(this.dataset.ends);
      if (Number.isNaN(this.fim)) return;

      injetarCSS();
      this.montarDOM();

      this.aoTrocarVisibilidade = () => {
        if (document.hidden) {
          this.pararTimer();
        } else {
          // Volta do segundo plano: recalcula antes de voltar a agendar,
          // senão o primeiro frame mostra o valor congelado de antes.
          this.render();
          this.agendar();
        }
      };
      document.addEventListener('visibilitychange', this.aoTrocarVisibilidade);

      this.render();
      this.agendar();
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.pararTimer();
      document.removeEventListener('visibilitychange', this.aoTrocarVisibilidade);
    }

    montarDOM() {
      const compacto = this.dataset.size === 'compact';
      this.compacto = compacto;
      this.textContent = '';

      const label = this.dataset.label;
      if (label) {
        const el = document.createElement('span');
        el.className = 'pc__label';
        el.textContent = label;
        this.appendChild(el);
      }

      const dot = document.createElement('span');
      dot.className = 'pc__dot';
      dot.setAttribute('aria-hidden', 'true');
      this.appendChild(dot);

      this.caixa = document.createElement('span');
      this.caixa.className = compacto ? 'pc__inline' : 'pc__blocks';
      // O visual é decorativo: quem usa leitor de tela recebe a frase única
      // do .pc__sr abaixo, atualizada só a cada minuto.
      this.caixa.setAttribute('aria-hidden', 'true');
      this.appendChild(this.caixa);

      const nomes = compacto
        ? { d: 'd', h: 'h', m: 'min', s: 's' }
        : { d: 'dias', h: 'horas', m: 'min', s: 'seg' };

      this.unidades = {};
      for (const chave of ['d', 'h', 'm', 's']) {
        const unidade = document.createElement('span');
        unidade.className = 'pc__unit';

        const digitos = document.createElement('span');
        digitos.className = 'pc__digits';

        const nome = document.createElement('span');
        nome.className = 'pc__unit-name';
        nome.textContent = nomes[chave];

        unidade.append(digitos, nome);
        this.caixa.appendChild(unidade);
        this.unidades[chave] = { unidade, digitos, valor: null };
      }

      // Frase para leitor de tela. `polite` + atualização por minuto: anunciar
      // de segundo em segundo tornaria a página inutilizável com NVDA/VoiceOver.
      this.sr = document.createElement('span');
      this.sr.className = 'pc__sr';
      this.sr.setAttribute('role', 'timer');
      this.sr.setAttribute('aria-live', 'polite');
      this.appendChild(this.sr);
      this.ultimoMinutoAnunciado = null;
    }

    /* Agenda o próximo tick na virada do segundo do relógio, não daqui a
       1000 ms — assim o contador não escorrega uns milissegundos por tick. */
    agendar() {
      this.pararTimer();
      const ateAVirada = SEGUNDO - (Date.now() % SEGUNDO);
      this.timer = window.setTimeout(() => {
        this.render();
        if (this.fim > Date.now()) this.agendar();
      }, ateAVirada);
    }

    pararTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    render() {
      const restante = this.fim - Date.now();

      if (restante <= 0) {
        this.expirar();
        return;
      }

      const dias = Math.floor(restante / DIA);
      const horas = Math.floor((restante % DIA) / HORA);
      const minutos = Math.floor((restante % HORA) / MINUTO);
      const segundos = Math.floor((restante % MINUTO) / SEGUNDO);

      this.aplicar('d', dias);
      this.aplicar('h', horas);
      this.aplicar('m', minutos);
      this.aplicar('s', segundos);

      // Enquanto sobra mais de um dia, o campo "dias" é o que importa e o
      // box de dias com "00" seria ruído — some até virar relevante.
      this.unidades.d.unidade.hidden = dias === 0;

      const calor =
        restante <= LIMITE_QUENTE ? 'hot' : restante <= LIMITE_MORNO ? 'warm' : 'calm';
      if (this.getAttribute('data-heat') !== calor) this.setAttribute('data-heat', calor);

      const minutoAtual = Math.floor(restante / MINUTO);
      if (minutoAtual !== this.ultimoMinutoAnunciado) {
        this.ultimoMinutoAnunciado = minutoAtual;
        const partes = [];
        if (dias) partes.push(`${dias} ${dias === 1 ? 'dia' : 'dias'}`);
        if (horas) partes.push(`${horas} ${horas === 1 ? 'hora' : 'horas'}`);
        partes.push(`${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`);
        this.sr.textContent = `${this.dataset.sr || 'Promoção termina em'} ${partes.join(', ')}.`;
      }
    }

    aplicar(chave, valor) {
      const unidade = this.unidades[chave];
      if (unidade.valor === valor) return;

      const primeiroRender = unidade.valor === null;
      unidade.valor = valor;
      unidade.digitos.textContent = doisDigitos(valor);

      if (primeiroRender || semMovimento() || !unidade.digitos.animate) return;
      unidade.digitos.animate(
        [
          { transform: 'translateY(-55%)', opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 },
        ],
        { duration: 220, easing: 'cubic-bezier(.2,.7,.3,1)' }
      );
    }

    expirar() {
      this.pararTimer();
      // Mesmo comportamento de antes: quem some é o bloco da promo inteiro
      // (tarja, banner, slide, upsell), não só o contador.
      const bloco = this.closest('[data-promo-expires]');
      if (bloco) {
        bloco.remove();
      } else {
        this.remove();
      }
    }
  }

  if (!customElements.get('promo-countdown')) {
    customElements.define('promo-countdown', PromoCountdown);
  }
})();
